import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Button, Table } from "reactstrap";
import moment from "moment";
import { formatPriceWithDollar } from "../../../utils/currency";

const paymentLineAmount = (value) => Number(value) || 0;

/** iDMS "Primary Loan Principal Only (not including interest)".
 *  prin + NEP + sales tax. Def down, late, misc, interest, and CPI are separate boxes. */
const sumPrimaryLoanBalance = (transaction, period) => {
  const suffix = period === "beg" ? "beg" : "end";
  return (
    paymentLineAmount(transaction?.[`principal_${suffix}_balance`]) +
    paymentLineAmount(transaction?.[`non_earn_prin_${suffix}_bal`]) +
    paymentLineAmount(transaction?.[`sales_tax_${suffix}_bal`])
  );
};

const ReceiptContent = ({ transaction, accountData }) => {
  const portfolio = accountData?.portfolio || {};
  const customer = accountData?.customer || {};
  const coBorrower = accountData?.co_borrower || null;
  const inventory = accountData?.worksheet?.inventory || {};
  const bookingId = accountData?.booking_id || "";
  const src = transaction?.originalData || transaction;

  const formatReceiptDateTime = (value) => {
    if (!value || value === "-") return "";
    const m = moment(value);
    return m.isValid() ? m.format("M/D/YYYY h:mm:ss A") : "";
  };
  const formatReceiptDate = (value) => {
    if (!value || value === "-") return "";
    const m = moment(value);
    return m.isValid() ? m.format("M/D/YYYY") : "";
  };

  const txDate = transaction?.date && transaction.date !== "-" ? transaction.date : src?.date;
  const effectDate =
    transaction?.effect_date && transaction.effect_date !== "-"
      ? transaction.effect_date
      : src?.effect_date;
  const receiptId =
    transaction?.receipt_id ||
    src?.receipt_id ||
    transaction?.refNumber ||
    transaction?.ref_number ||
    src?.ref_number ||
    transaction?.id ||
    src?.id ||
    "";

  // Support both raw API (snake_case) and normalized (camelCase) formats
  const defDownBegBal = transaction?.def_down_beg_bal ?? 0;
  const defDownEndBal = transaction?.def_down_end_bal ?? 0;

  const paymentForm = transaction?.paymentForm || transaction?.payment_form || "";
  const refNumber = transaction?.refNumber || transaction?.ref_number || "";
  const deliveryMethod = transaction?.deliveryMethod || transaction?.delivery_method || "";

  const cpiAmount = transaction?.cpi_amount || 0;
  const cpiBegBal = transaction?.cpi_beg_bal || 0;
  const cpiEndBal = transaction?.cpi_end_bal || 0;
  const hasCPI = cpiAmount > 0 || cpiBegBal > 0 || cpiEndBal > 0;

  const primaryLoanBegBal = sumPrimaryLoanBalance(transaction, "beg");
  const primaryLoanEndBal = sumPrimaryLoanBalance(transaction, "end");

  // Next scheduled payment from accountData.remaining_payments
  const nextPayment = Array.isArray(accountData?.remaining_payments) && accountData.remaining_payments.length > 0
    ? accountData.remaining_payments[0]
    : null;

  return (
    <div className="receipt-print-root" style={{ fontSize: "11px", color: "#333", backgroundColor: "#fff" }}>
      <style>{`
        .receipt-print-root,
        .receipt-print-root table,
        .receipt-print-root .table,
        .receipt-print-root .table > thead > tr > th,
        .receipt-print-root .table > tbody > tr > td,
        .receipt-print-root .table > tbody > tr > th,
        .receipt-print-root .text-muted {
          color: #333 !important;
        }
        .receipt-print-root .table > tbody > tr {
          cursor: default !important;
        }
        .receipt-print-root .table > tbody > tr > td {
          cursor: default !important;
        }
      `}</style>
      {/* Portfolio and Receipt ID Table */}
      <Row className="mb-4">
        <Col xs="6">
          <div style={{ fontSize: "12px" }}>
            <strong>{portfolio.name || "COMPANY NAME"}</strong><br />
            {portfolio.address || ""}<br />
            {portfolio.city || ""}, {portfolio.state || ""} {portfolio.zip || ""}<br />
            {portfolio.phone || ""} phone<br />
            {portfolio.fax || ""} fax<br />
            {portfolio.website || ""}<br /><br />
            <strong className="mt-2 text-uppercase">Thanks for your business!</strong>
          </div>
        </Col>
        <Col xs="6">
          <Table bordered size="sm" className="receipt-ink" style={{ border: "1px solid #000080", color: "#333" }}>
            <tbody>
              <tr>
                <td style={{ width: "45%", backgroundColor: "#f2f2f2", fontWeight: "600", borderColor: "#000080", color: "#333" }}>Receipt ID</td>
                <td style={{ borderColor: "#000080", color: "#333" }}>{receiptId}</td>
              </tr>
              <tr>
                <td style={{ backgroundColor: "#f2f2f2", fontWeight: "600", borderColor: "#000080" }}>Transaction Date</td>
                <td style={{ borderColor: "#000080" }}>{formatReceiptDateTime(txDate)}</td>
              </tr>
              <tr>
                <td style={{ backgroundColor: "#f2f2f2", fontWeight: "600", borderColor: "#000080" }}>Effective Date</td>
                <td style={{ borderColor: "#000080" }}>{formatReceiptDate(effectDate || txDate)}</td>
              </tr>
            </tbody>
          </Table>
        </Col>
      </Row>

      {/* Customer and Account Info */}
      <div className="mb-4" style={{ border: "1px solid #000080" }}>
        <Row className="m-0">
          <Col xs="6" className="p-2 border-right" style={{ borderColor: "#000080" }}>
            <strong className="text-muted">Customer</strong><br />
            <div style={{ fontSize: "12px" }}>
              {customer.full_name || customer.name || ""}<br />
              {customer.address || ""}<br /><br />
              {customer.city || ""}, {customer.state || ""} {customer.zip || ""}<br />
              Co-Borrower: {coBorrower ? (coBorrower.full_name || coBorrower.name) : ""}
            </div>
          </Col>
          <Col xs="6" className="p-2">
            <strong className="text-muted">Account Information</strong><br />
            <div style={{ fontSize: "12px" }}>
              Loan: {inventory.stock_number || ""}<br />
              Account: {bookingId}<br />
              Stock #/VIN: {inventory.stock_number || ""} / {inventory.vin || ""}<br />
              Description: {inventory.vehicle_description || inventory.name || ""}
            </div>
          </Col>
        </Row>
      </div>

      {/* Posted By and Notes */}
      <Row className="mb-4">
        <Col xs="6">
          <strong>Transaction Posted By</strong><br />
          {transaction.posted_by || ""}
        </Col>
        <Col xs="6">
          <strong>Notes</strong><br />
          {transaction.note || transaction.description || ""}
        </Col>
      </Row>

      {/* Transaction Type Table — iDMS style: DP / Misc / Late / Regular / CPI from payment fields */}
      {(() => {
        const n = (v) => {
          const x = Math.abs(Number(v) || 0);
          return Number.isFinite(x) ? Number(x.toFixed(2)) : 0;
        };
        const typeLabel = (type, note) => {
          if (type === "def_down") return { label: "Deferred Down Payment", desc: "Primary Loan" };
          if (type === "regular_payment") return { label: "Regular Payment", desc: "Primary Loan" };
          if (type === "reversal") return { label: "Reversal", desc: "Primary Loan" };
          if (type === "def_down_reversal") return { label: "Down Payment Reversal", desc: "Down Payment" };
          if (type === "cpi") return { label: "Collateral Insurance Payment", desc: note || "CPI PAYMENT" };
          if (type === "late_fees" || type === "late_fee") return { label: "Late Fee Payment", desc: "Primary Loan" };
          if (type === "late_fee_waived") return { label: "Late Fee Waived", desc: "Primary Loan" };
          if (type === "misc_fee" || type === "misc") return { label: "Misc Payment", desc: "Primary Loan" };
          return { label: type || "Payment", desc: "Primary Loan" };
        };

        const gross = n(transaction?.amount ?? src?.amount);
        const defDownLedger = n(transaction?.def_down_collected ?? src?.def_down_collected);
        const defDownAdj = Number(transaction?.def_down_adjusted ?? src?.def_down_adjusted);
        // iDMS receipt "Deferred Down" = cash to DP type (ledger collected + tax leave when adj < 0)
        const receiptAlloc =
          transaction?.migration_receipt_allocation ||
          src?.migration_receipt_allocation ||
          transaction?.receipt_allocation ||
          src?.receipt_allocation ||
          transaction?.external_payment?.migration_receipt_allocation ||
          src?.external_payment?.migration_receipt_allocation ||
          transaction?.originalData?.external_payment?.migration_receipt_allocation;
        const defDown =
          receiptAlloc?.def_down != null && Number(receiptAlloc.def_down) > 0.009
            ? n(receiptAlloc.def_down)
            : defDownAdj < -0.009
              ? Number((defDownLedger + Math.abs(defDownAdj)).toFixed(2))
              : defDownLedger;
        const misc = n(
          receiptAlloc?.misc_fee != null && Number(receiptAlloc.misc_fee) > 0.009
            ? receiptAlloc.misc_fee
            : transaction?.misc_fee_collected ?? transaction?.misc_fees_collected
        );
        let late = n(
          receiptAlloc?.late_fee != null
            ? receiptAlloc.late_fee
            : transaction?.late_fees_amount ?? transaction?.lateFeesAmount
        );
        const cpi = n(
          receiptAlloc?.cpi != null && Number(receiptAlloc.cpi) > 0.009
            ? receiptAlloc.cpi
            : transaction?.cpi_amount
        );
        // Old migration bug: Misc Payment $235 also landed in late_fees_amount
        if (misc > 0.009 && Math.abs(late - misc) < 0.02) late = 0;
        // Prefer iDMS receipt Regular Payment when stored; else cash remainder
        const regularFromReceipt =
          receiptAlloc?.regular_payment != null ? n(receiptAlloc.regular_payment) : null;
        const regular =
          regularFromReceipt != null && regularFromReceipt > 0.009
            ? regularFromReceipt
            : Math.max(
                0,
                Number((gross - defDown - misc - late - cpi).toFixed(2))
              );

        const fromFields = [];
        if (defDown > 0.009) fromFields.push({ type: "def_down", total: defDown, ...typeLabel("def_down") });
        if (misc > 0.009) fromFields.push({ type: "misc_fee", total: misc, ...typeLabel("misc_fee") });
        if (late > 0.009) fromFields.push({ type: "late_fees", total: late, ...typeLabel("late_fees") });
        if (regular > 0.009) fromFields.push({ type: "regular_payment", total: regular, ...typeLabel("regular_payment") });
        if (cpi > 0.009) fromFields.push({ type: "cpi", total: cpi, ...typeLabel("cpi") });

        // Prefer schedule detail only when it already has a Regular/CPI/Late row (complete migration detail)
        const detail = Array.isArray(transaction?.detail) ? transaction.detail : [];
        const groupedFromDetail = Object.values(
          detail.reduce((acc, item) => {
            const key = item.type || "unknown";
            if (!acc[key]) acc[key] = { type: key, total: 0, ...typeLabel(key, item.note) };
            acc[key].total += Number(item.payment_applied || 0);
            return acc;
          }, {})
        ).filter((r) => Number(r.total) > 0.009);

        const detailHasRegular = groupedFromDetail.some((r) => r.type === "regular_payment");
        const detailHasLate = groupedFromDetail.some(
          (r) => r.type === "late_fees" || r.type === "late_fee"
        );
        const detailSum = groupedFromDetail.reduce((s, r) => s + Number(r.total || 0), 0);
        const detailLooksComplete =
          groupedFromDetail.length > 0 &&
          (detailHasRegular || detailHasLate) &&
          Math.abs(detailSum - gross) <= 1.01;

        // Eski fişler: detail yalnızca regular_payment iken aslında late fee ödemesi
        let normalizedDetail = groupedFromDetail;
        if (
          detailLooksComplete &&
          detailHasRegular &&
          !detailHasLate &&
          late > 0.009 &&
          n(transaction?.collected ?? transaction?.principal_collected) < 0.01
        ) {
          normalizedDetail = groupedFromDetail
            .map((row) => {
              if (row.type !== "regular_payment") return row;
              const regularTotal = n(row.total);
              if (regularTotal <= late + 0.009) {
                return { ...row, type: "late_fees", ...typeLabel("late_fees") };
              }
              return { ...row, total: Number((regularTotal - late).toFixed(2)) };
            })
            .filter((row) => n(row.total) > 0.009);
          if (late > 0.009 && !normalizedDetail.some((r) => r.type === "late_fees")) {
            normalizedDetail.push({ type: "late_fees", total: late, ...typeLabel("late_fees") });
          }
        }

        // Prefer iDMS receipt allocation when schedule detail is missing or incomplete
        const receiptHasSplit =
          receiptAlloc &&
          (Number(receiptAlloc.regular_payment) > 0.009 ||
            Number(receiptAlloc.cpi) > 0.009 ||
            Number(receiptAlloc.late_fee) > 0.009 ||
            Number(receiptAlloc.def_down) > 0.009 ||
            Number(receiptAlloc.misc_fee) > 0.009);

        const rows = receiptHasSplit && !detailLooksComplete
          ? fromFields
          : detailLooksComplete
            ? normalizedDetail
            : fromFields;

        return (
          <Table size="sm" className="mb-4 receipt-ink">
            <thead>
              <tr className="border-bottom" style={{ borderBottomWidth: "2px" }}>
                <th style={{ border: "none" }}>Transaction Type</th>
                <th style={{ border: "none" }}>Balance Desc</th>
                <th className="text-right" style={{ border: "none" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? (
                rows.map((row) => (
                  <tr key={row.type} className="border-bottom">
                    <td className="py-2">{row.label}</td>
                    <td className="py-2">{row.desc}</td>
                    <td className="text-right py-2">{formatPriceWithDollar(row.total)}</td>
                  </tr>
                ))
              ) : (
                <tr className="border-bottom">
                  <td className="py-2">
                    {/write\s*down/i.test(String(transaction?.payment_type || transaction?.paymentForm || paymentForm || ""))
                      ? "Write Down"
                      : transaction?.description || "Posted Payment"}
                  </td>
                  <td className="py-2">
                    {hasCPI && /write\s*down|cpi/i.test(String(transaction?.note || transaction?.description || ""))
                      ? "CPI"
                      : "Primary Loan"}
                  </td>
                  <td className="text-right py-2">{formatPriceWithDollar(gross)}</td>
                </tr>
              )}
            </tbody>
          </Table>
        );
      })()}

      {/* Totals Section */}
      <div className="d-flex justify-content-end">
        <div style={{ width: "380px" }}>
          <div className="d-flex justify-content-between mb-3 pt-1" style={{ borderTop: "2px solid #333" }}>
            <span className="font-weight-bold">TOTAL TRANSACTION:</span>
            <strong style={{ fontSize: "1.1rem" }}>{formatPriceWithDollar(transaction.amount)}</strong>
          </div>

          <div className="border border-dark p-3" style={{ background: "#fcfcfc" }}>
            <div className="d-flex justify-content-between mb-1">
              <span>Amount Paid :</span>
              <strong>{formatPriceWithDollar(transaction.amount)}</strong>
            </div>
            <div className="d-flex justify-content-between mb-1">
              <span>Amount Received :</span>
              <strong>{formatPriceWithDollar(transaction.amount)}</strong>
            </div>
            <div className="d-flex justify-content-between mb-3 pb-1 border-bottom">
              <span>Change Due :</span>
              <strong>$0.00</strong>
            </div>

            <Row className="mt-2" style={{ fontSize: "12px" }}>
              <Col xs="6" className="text-right text-muted">Payment Method :</Col>
              <Col xs="6"><strong>{paymentForm}</strong></Col>

              <Col xs="6" className="text-right text-muted">Reference # :</Col>
              <Col xs="6"><strong>{refNumber}</strong></Col>

              <Col xs="6" className="text-right text-muted">Delivery Method :</Col>
              <Col xs="6"><strong>{deliveryMethod}</strong></Col>
            </Row>
          </div>
        </div>
      </div>

      {/* Breakdown Sections */}
      <div className="mt-5 mb-2 p-1 font-weight-bold" style={{ backgroundColor: "#f0f0f0", border: "1px solid #ddd", fontSize: "11px" }}>
        CURRENT DUE BREAKDOWN - as of this transaction
      </div>
      <div className="d-flex justify-content-end pr-3">
        <div style={{ width: "350px" }}>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span>Next Scheduled Payment:</span>
            <div style={{ border: "1px solid #ddd", width: "120px", textAlign: "center", padding: "2px", backgroundColor: "#f9f9f9" }}>
              {nextPayment ? formatPriceWithDollar(nextPayment.amount) : "$0.00"}
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <span>Next Schedule Payment Date:</span>
            <div style={{ border: "1px solid #ddd", width: "120px", textAlign: "center", padding: "2px", backgroundColor: "#f9f9f9" }}>
              {nextPayment?.date ? moment(nextPayment.date).format("M/D/YYYY") : "-"}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 mb-2 p-1 font-weight-bold" style={{ backgroundColor: "#f0f0f0", border: "1px solid #ddd", fontSize: "11px" }}>
        BALANCE INFORMATION - as of this transaction
      </div>
      <Row className="m-0 border" style={{ borderColor: "#ddd" }}>
        <Col xs="6" className="p-0 border-right" style={{ borderColor: "#ddd" }}>
          <div className="p-1 font-weight-bold" style={{ backgroundColor: "#e8e6dd", fontSize: "11px" }}>Primary Loan Principal Only Balance</div>
          <div className="p-2">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="text-right lh-1" style={{ fontSize: "11px" }}>Before Transaction<br /><small>(not including interest)</small></span>
              <div style={{ border: "1px dashed #999", width: "95px", textAlign: "center", backgroundColor: "#f9f9f9" }}>
                {formatPriceWithDollar(primaryLoanBegBal)}
              </div>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-right lh-1" style={{ fontSize: "11px" }}>After Transaction<br /><small>(not including interest)</small></span>
              <div style={{ border: "1px solid #333", width: "95px", textAlign: "center", backgroundColor: "#f9f9f9" }}>
                {formatPriceWithDollar(primaryLoanEndBal)}
              </div>
            </div>
          </div>
        </Col>
        <Col xs="6" className="p-0">
          <div className="p-1 font-weight-bold" style={{ backgroundColor: "#e8e6dd", fontSize: "11px" }}>Down Payment(s) Balances</div>
          <div className="p-2">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span style={{ fontSize: "11px" }}>Before Transaction</span>
              <div style={{ border: "1px dashed #999", width: "95px", textAlign: "center", backgroundColor: "#f9f9f9" }}>
                {formatPriceWithDollar(defDownBegBal)}
              </div>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <span style={{ fontSize: "11px" }}>After Transaction</span>
              <div style={{ border: "1px dashed #999", width: "95px", textAlign: "center", backgroundColor: "#f9f9f9" }}>
                {formatPriceWithDollar(defDownEndBal)}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {hasCPI && (
        <>
          <div className="mt-3 mb-2 p-1 font-weight-bold" style={{ backgroundColor: "#f0f0f0", border: "1px solid #ddd", fontSize: "11px" }}>
            {/write\s*down/i.test(String(transaction?.payment_type || transaction?.paymentForm || paymentForm || ""))
              ? "BALANCE INFORMATION — Side Note (CPI)"
              : "CPI BALANCE INFORMATION"}
          </div>
          <Row className="m-0 border" style={{ borderColor: "#ddd" }}>
            <Col xs="6" className="p-0 border-right" style={{ borderColor: "#ddd" }}>
              <div className="p-1 font-weight-bold" style={{ backgroundColor: "#e8e6dd", fontSize: "11px" }}>
                {/write\s*down/i.test(String(transaction?.payment_type || transaction?.paymentForm || paymentForm || ""))
                  ? "Side Note Balances"
                  : "CPI Balance"}
              </div>
              <div className="p-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span style={{ fontSize: "11px" }}>Before Transaction</span>
                  <div style={{ border: "1px dashed #999", width: "95px", textAlign: "center", backgroundColor: "#f9f9f9" }}>
                    {formatPriceWithDollar(cpiBegBal)}
                  </div>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span style={{ fontSize: "11px" }}>After Transaction</span>
                  <div style={{ border: "1px solid #333", width: "95px", textAlign: "center", backgroundColor: "#f9f9f9" }}>
                    {formatPriceWithDollar(cpiEndBal)}
                  </div>
                </div>
              </div>
            </Col>
            <Col xs="6" className="p-0">
              <div className="p-1 font-weight-bold" style={{ backgroundColor: "#e8e6dd", fontSize: "11px" }}>CPI Amount Collected</div>
              <div className="p-2">
                <div className="d-flex justify-content-between align-items-center">
                  <span style={{ fontSize: "11px" }}>
                    {/write\s*down/i.test(String(transaction?.payment_type || transaction?.paymentForm || paymentForm || ""))
                      ? "CPI Write Down"
                      : "CPI Payment"}
                  </span>
                  <div style={{ border: "1px dashed #999", width: "95px", textAlign: "center", backgroundColor: "#f9f9f9" }}>
                    {formatPriceWithDollar(cpiAmount)}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

const ReceiptDrawer = ({ isOpen, onClose, transaction, accountData }) => {
  const [visible, setVisible] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      setTimeout(() => setInternalOpen(true), 10);
    } else {
      setInternalOpen(false);
      setTimeout(() => setVisible(false), 300);
    }
  }, [isOpen]);

  if (!visible || !transaction) return null;

  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`
      <html>
        <head>
          <title>Receipt - ${transaction.refNumber || transaction.id}</title>
          <link rel="stylesheet" href="https://stackpath.bootstrapcdn.com/bootstrap/4.5.2/css/bootstrap.min.css">
          <style>
            @media print {
              @page { margin: 10mm; }
              body { 
                padding: 10mm;
                background: white !important;
                color: black !important;
                -webkit-print-color-adjust: exact;
              }
              .no-print { display: none !important; }
            }
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; }
            .table-bordered { border: 1px solid #dee2e6 !important; }
            .table-bordered td, .table-bordered th { border: 1px solid #dee2e6 !important; }
          </style>
        </head>
        <body>
          ${printContents}
        </body>
      </html>
    `);
    doc.close();

    // Give images and styles time to load if any
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 500);
    }, 500);
  };

  return (
    <>
      {/* Overlay */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0,0,0,0.5)",
          zIndex: 2500,
          opacity: internalOpen ? 1 : 0,
          transition: "opacity 300ms ease",
        }}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: "60vw",
          minWidth: "650px",
          maxWidth: "950px",
          height: "100vh",
          backgroundColor: "#fff",
          color: "#333",
          boxShadow: "-8px 0 24px rgba(0,0,0,0.4)",
          zIndex: 2501,
          display: "flex",
          flexDirection: "column",
          transform: internalOpen ? "translateX(0)" : "translateX(100%)",
          transition: "transform 300ms ease",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "12px 20px",
            backgroundColor: "#1d8cf8",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h4 className="mb-0" style={{ color: "white", fontSize: "1.2rem", fontWeight: "700" }}>POSTED PAYMENT</h4>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "white",
              fontSize: "1.5rem",
              lineHeight: 1,
              cursor: "pointer"
            }}
          >
            &times;
          </button>
        </div>

        {/* Action Buttons Toolbar */}
        <div className="p-3 border-bottom d-flex justify-content-center" style={{ backgroundColor: "#f8f9fa" }}>
          <Button color="info" className="mr-2" onClick={handlePrint} style={{ fontWeight: "700" }}>
            PRINT THIS RECEIPT
          </Button>
          <Button outline color="secondary" onClick={onClose} style={{ fontWeight: "700" }}>
            CLOSE
          </Button>
        </div>

        {/* Receipt Content Container */}
        <div style={{ padding: "30px", overflowY: "auto", flex: 1, backgroundColor: "#fff" }}>
          <div ref={printRef}>
            <ReceiptContent transaction={transaction} accountData={accountData} />
          </div>
        </div>
      </div>
    </>
  );
};

export default ReceiptDrawer;
